import { SuggestionPrimitive, useAuiState } from '@assistant-ui/react';
import { BookOpenText, ChartColumnIncreasing, FilePenLine } from 'lucide-react';
import { starterPromptKind } from '@/lib/starter-prompts';
import { useOptionalWorkspace } from './context';
import { WorkspaceIcon } from './icons';
import { startingPointIcon, type StartingPointIconName } from './starting-point-icon';
import { useContext, type ReactNode } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { StarterSuggestionsContext } from '@/lib/starter-suggestions';
import { ConnectionLogo } from './connection-logo';

const questionIcons = {
  knowledge: BookOpenText,
  research: ChartColumnIncreasing,
  enrich: FilePenLine,
};

function StartingPointIcon({ name }: { name: StartingPointIconName }) {
  // Skills worth adding and App ideas wear their place's own mark, the one its
  // page and canvas already use, rather than a question's icon.
  if (name === 'skill' || name === 'app')
    return (
      <WorkspaceIcon
        name={name === 'skill' ? 'skills' : 'app'}
        size={20}
        className="ws-starting-point-icon"
      />
    );
  const Icon = questionIcons[name];
  return (
    <Icon size={20} strokeWidth={1.65} aria-hidden="true" className="ws-starting-point-icon" />
  );
}

/** A starting point's parts: its mark, its words and the arrow. One card under the composer and on a place's page. */
function StartingPointParts({
  icon,
  title,
  description,
  children,
}: {
  icon: ReactNode;
  title: ReactNode;
  description: ReactNode;
  children?: ReactNode;
}) {
  return (
    <>
      {icon}
      <span className="ws-starting-point-copy">
        {title}
        {description}
        {children}
      </span>
      <ArrowUpRight size={15} className="ws-home-task-arrow" aria-hidden="true" />
    </>
  );
}

/** A starting point outside a chat (the Apps page's ideas): the card from under the composer, as its own button. */
export function StartingPointCard({
  icon,
  title,
  description,
  onSelect,
}: {
  icon: StartingPointIconName;
  title: string;
  description: string;
  onSelect: () => void;
}) {
  return (
    <button type="button" className="aui-thread-welcome-suggestion" onClick={onSelect}>
      <StartingPointParts
        icon={<StartingPointIcon name={icon} />}
        title={<span className="ws-starting-point-question">{title}</span>}
        description={<span className="ws-starting-point-description">{description}</span>}
      />
    </button>
  );
}

/** Presentation only: the native suggestion trigger retains its prompt and send/auth gates. */
export function WorkspaceWelcomeSuggestion() {
  const workspace = useOptionalWorkspace();
  const kind = starterPromptKind(workspace?.turnContext.intent);
  const { prompts } = useContext(StarterSuggestionsContext);
  const text = useAuiState((state) => state.suggestion.prompt);
  const card = prompts.find((prompt) => prompt.text === text);
  // Selecting a plain name keeps this state read reference-stable across renders.
  const name = useAuiState((state) => {
    const suggestions = state.suggestions.suggestions;
    const index = suggestions.findIndex(({ prompt }) => prompt === state.suggestion.prompt);
    return startingPointIcon(kind, index, suggestions.length);
  });
  return (
    <StartingPointParts
      icon={
        card?.icon ? (
          <span className={`ws-home-task-icon ws-home-task-${card.icon}`}>
            <WorkspaceIcon name={card.icon === 'research' ? 'globe' : card.icon} size={19} />
          </span>
        ) : (
          <StartingPointIcon name={name} />
        )
      }
      title={<SuggestionPrimitive.Title className="ws-starting-point-question" />}
      description={<SuggestionPrimitive.Description className="ws-starting-point-description" />}
    >
      {Boolean(card?.connections?.length) && (
        <span className="ws-home-task-apps">
          {card!.connections!.map((connection) => (
            <span key={connection.id}>
              <ConnectionLogo {...connection} />
              <span>{connection.name}</span>
            </span>
          ))}
        </span>
      )}
    </StartingPointParts>
  );
}
